/**
 * 文本类 op：内容（单行 / 多行 tspan / 逐字竖排）、字体属性、显隐。
 *
 * 内容改动会动结构（tspan 数量），所以统一用**元素快照 patch**：
 * apply 写最终形态，revert 退回编辑前元素内部 XML —— 一次编辑精确对应一条 undo。
 */
import { SVG_NS, pathOf } from '../dom';
import type { Command } from '../history';
import { attrPatch, makeStructuralPatch, snapshotOf, type Patch } from '../patch';
import {
  LINE_HEIGHT_RATIO,
  VERTICAL_RATIO,
  isTextLike,
  readTextProps,
  textLines,
  textOwner,
} from '../query/text';

const noop = (label: string): Command => ({ label, patches: [] });

/** 数值属性读取（缺失/非法都归 0） */
const numAttr = (el: Element, name: string): number => {
  const v = Number.parseFloat(el.getAttribute(name) ?? '');
  return Number.isFinite(v) ? v : 0;
};

/**
 * 把「若干行文本」写成 DOM 的规范形式：
 *  · 1 行 → 直接放文本节点（去掉 tspan，回到最简）
 *  · N 行 → 逐行 `<tspan>`，显式 `y` 步进（不依赖 dy，渲染更可预期）
 *  · 给了 columnX（竖排）→ 每个 tspan 显式 `x`，形成一列
 */
export function writeTextLines(
  el: Element,
  lines: readonly string[],
  lineHeight: number,
  columnX?: number,
): void {
  const clean = lines.length === 0 ? [''] : [...lines];
  const first = Array.from(el.children).find((c) => c.tagName.toLowerCase() === 'tspan');
  const doc = el.ownerDocument;
  const baseY = numAttr(el, 'y');
  const baseX = columnX ?? numAttr(el, 'x');

  while (el.firstChild) el.removeChild(el.firstChild);

  if (clean.length === 1) {
    el.appendChild(doc.createTextNode(clean[0] as string));
    return;
  }

  clean.forEach((text, i) => {
    const span = first ? (first.cloneNode(false) as Element) : doc.createElementNS(SVG_NS, 'tspan');
    span.removeAttribute('dy');
    span.removeAttribute('y');
    span.removeAttribute('x');
    if (columnX !== undefined) span.setAttribute('x', String(baseX));
    span.setAttribute('y', String(baseY + i * lineHeight));
    span.textContent = text;
    el.appendChild(span);
  });
}

/** 单行/多行内容（行数变化时重建 tspan） */
/**
 * 改文字内容。
 *
 * `mergeKey` 是给**面板里边打边写**用的：每次按键都写文档，但整段输入合并成一条 undo
 * （与滑杆同一套合并规则）。内容没变则返回空命令 —— 实时输入会连发 input，
 * 不挡住就会出现一堆"没改动"的历史条目。
 */
export function setTextLines(_root: Element, el: Element, lines: readonly string[], mergeKey?: string): Command {
  const target = textOwner(el) ?? el;
  if (!isTextLike(target)) return noop('文字');
  // 拼接后的文本相同 = 内容没变（竖排/多行情况下这个比较只会偏保守，即多提交一次，不会漏提交）
  if ((target.textContent ?? '') === lines.join('')) return noop('文字');
  const before = snapshotOf(target);
  const height = readTextProps(target).fontSize * LINE_HEIGHT_RATIO;
  const after = lines;
  const patch: Patch = makeStructuralPatch(target, before, '文字内容', () => {
    writeTextLines(target, after, height);
  });
  return { label: '文字内容', patches: [patch], mergeKey };
}

export interface TextAttrChanges {
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: 'normal' | 'bold';
  fontStyle?: 'normal' | 'italic';
  fill?: string | null;
  textAnchor?: 'start' | 'middle' | 'end';
  letterSpacing?: number;
}

const ATTR_NAME: Record<keyof TextAttrChanges, string> = {
  fontFamily: 'font-family',
  fontSize: 'font-size',
  fontWeight: 'font-weight',
  fontStyle: 'font-style',
  fill: 'fill',
  textAnchor: 'text-anchor',
  letterSpacing: 'letter-spacing',
};

/** 字体/颜色/对齐/字间距 —— 都是属性改动，走细粒度 attr patch */
export function setTextAttrs(
  root: Element,
  els: Iterable<Element>,
  changes: TextAttrChanges,
  mergeKey?: string,
): Command {
  const patches: Patch[] = [];
  for (const el of els) {
    const target = textOwner(el) ?? el;
    for (const key of Object.keys(changes) as (keyof TextAttrChanges)[]) {
      const value = changes[key];
      if (value === undefined) continue;
      const name = ATTR_NAME[key];
      const before = target.getAttribute(name);
      const after = value === null ? null : String(value);
      const path = pathOf(target, root);
      const patch = path ? attrPatch(path, name, before, after) : null;
      if (patch) patches.push(patch);
    }
  }
  return { label: '文字样式', patches, mergeKey };
}

/**
 * 竖排开关：ON 时逐字拆成 `<tspan>`（每字一个，x 固定成列），OFF 时并回单行。
 * 只有**真的需要拆**时才改结构；两种方向互为逆操作 → revert 直接再做一次反向即可。
 */
export function setVertical(_root: Element, els: Iterable<Element>, vertical: boolean): Command {
  const patches: Patch[] = [];
  for (const el of els) {
    const target = textOwner(el) ?? el;
    if (!isTextLike(target)) continue;
    if (readTextProps(target).vertical === vertical) continue;

    const before = snapshotOf(target);
    const props = readTextProps(target);
    const step = props.fontSize * VERTICAL_RATIO;

    if (vertical) {
      const glyphs = [...props.content.replace(/\n/g, '')];
      const columnX = Number(target.getAttribute('x') ?? '0') || 0;
      patches.push(
        makeStructuralPatch(target, before, '竖排', () => {
          writeTextLines(target, glyphs.length > 0 ? glyphs : [''], step, columnX);
        }),
      );
    } else {
      const joined = props.lines.join('');
      patches.push(
        makeStructuralPatch(target, before, '横排', () => {
          writeTextLines(target, [joined], step);
        }),
      );
    }
  }
  return { label: vertical ? '竖排' : '横排', patches };
}

/** 显隐（图层列表的「眼睛」） */
export function setHidden(root: Element, els: Iterable<Element>, hidden: boolean): Command {
  const patches: Patch[] = [];
  for (const el of els) {
    const path = pathOf(el, root);
    if (!path) continue;
    const before = el.getAttribute('display');
    const patch = attrPatch(path, 'display', before, hidden ? 'none' : null);
    if (patch) patches.push(patch);
  }
  return { label: hidden ? '隐藏' : '显示', patches };
}

/** 供「文字」分区显示用：当前选区里第一个文本元素 */
export function firstTextOf(els: readonly Element[]): Element | null {
  for (const el of els) {
    const owner = textOwner(el) ?? el;
    if (isTextLike(owner)) return owner;
  }
  return null;
}

export { textLines };
